import React from 'react';
import { ShoppingBag, MessageCircle, TrendingUp, MapPin, Package, RefreshCw, LayoutGrid } from 'lucide-react';
import { useGetStatsQuery, type StatsWhatsAppLead } from '../store/statsApi';

function fmtARS(n: number): string {
  return `$${n.toLocaleString('es-AR')}`;
}

function fmtRelative(unix: number): string {
  const diff = Math.floor(Date.now() / 1000) - unix;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
}

function KpiCard({
  label, value, icon: Icon, sub, positive,
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  sub?: string;
  positive?: boolean;
}) {
  return (
    <div className="bg-white p-4 lg:p-6 rounded-2xl border border-page-border shadow-sm flex flex-col justify-between relative overflow-hidden group animate-hover">
      <div className="absolute top-0 right-0 w-24 h-24 bg-brand-surface rounded-full blur-3xl -mr-4 -mt-4" />
      <div className="flex items-start justify-between mb-3">
        <span className="text-[10px] lg:text-xs font-semibold text-ink-muted uppercase tracking-wide leading-tight pr-2">{label}</span>
        <div className="bg-brand-surface p-2 rounded-xl border border-brand-border flex-shrink-0">
          <Icon className="w-3.5 h-3.5 text-brand-primary" />
        </div>
      </div>
      <div>
        <h3 className="font-heading font-bold text-xl lg:text-2xl text-ink">{value}</h3>
        {sub && (
          <span className={`text-[10px] font-bold mt-1 block ${positive === false ? 'text-rose-600' : 'text-emerald-600'}`}>
            {sub}
          </span>
        )}
      </div>
    </div>
  );
}

function LeadRow({ lead }: { lead: StatsWhatsAppLead }) {
  let itemNames = '—';
  try {
    const parsed = JSON.parse(lead.items) as { name: string; quantity: number; size: string }[];
    itemNames = parsed.map(i => `${i.name} (T.${i.size} ×${i.quantity})`).join(', ');
  } catch { /* ignore */ }

  return (
    <div className="flex items-start justify-between gap-3 p-3 rounded-xl bg-page-surface border border-page-border">
      <div className="flex items-start gap-3 min-w-0">
        <div className="bg-emerald-50 border border-emerald-100 p-2 rounded-lg flex-shrink-0 mt-0.5">
          <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-semibold text-ink leading-tight">{lead.customerName}</p>
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${lead.deliveryType === 'envio'
              ? 'bg-blue-50 text-blue-600 border-blue-200'
              : 'bg-slate-50 text-slate-600 border-slate-200'
              }`}>
              {lead.deliveryType === 'envio'
                ? <><MapPin className="w-2.5 h-2.5" /> Envío</>
                : <><Package className="w-2.5 h-2.5" /> Retiro</>}
            </span>
          </div>
          <p className="text-[10px] text-ink-muted mt-0.5 leading-tight line-clamp-1">{itemNames}</p>
        </div>
      </div>
      <div className="flex-shrink-0 text-right">
        <p className="text-xs font-bold text-ink">{fmtARS(lead.totalAmount)}</p>
        <p className="text-[10px] text-ink-faint mt-0.5">{fmtRelative(lead.createdAt)}</p>
      </div>
    </div>
  );
}

export const Dashboard: React.FC = () => {
  const { data: stats, isLoading, isFetching, refetch } = useGetStatsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const dash = (n: number | undefined) => n !== undefined ? n : '—';

  return (
    <div className="space-y-6">

      {/* Header desktop */}
      <header className="hidden lg:flex justify-between items-center border-b border-page-border pb-5">
        <div>
          <span className="text-xs font-semibold text-brand-primary uppercase tracking-widest">Resumen General</span>
          <h1 className="font-heading font-bold text-3xl text-ink mt-1">Panel de Control</h1>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium bg-page-surface hover:bg-page-panel border border-page-border text-ink-muted hover:text-ink transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </header>

      {/* Header mobile */}
      <div className="lg:hidden flex items-center justify-between">
        <div>
          <span className="text-xs font-semibold text-brand-primary uppercase tracking-widest">Resumen</span>
          <h1 className="font-heading font-bold text-2xl text-ink mt-0.5">Panel de Control</h1>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-2 text-ink-muted hover:text-ink rounded-xl border border-page-border bg-page-surface transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
        <KpiCard
          label="Productos activos"
          value={isLoading ? '…' : dash(stats?.products.active)}
          icon={ShoppingBag}
          sub={stats ? `${stats.products.total} total` : undefined}
        />
        <KpiCard
          label="Categorías"
          value={isLoading ? '…' : dash(stats?.categories.total)}
          icon={LayoutGrid}
        />
        <KpiCard
          label="Pedidos WhatsApp (7d)"
          value={isLoading ? '…' : dash(stats?.whatsappLeads.last7d)}
          icon={MessageCircle}
          sub={stats ? `${stats.whatsappLeads.last30d} en 30 días` : undefined}
          positive
        />
        <KpiCard
          label="Pedidos WhatsApp total"
          value={isLoading ? '…' : dash(stats?.whatsappLeads.total)}
          icon={TrendingUp}
          positive
        />
      </div>

      {/* CF Analytics link */}
      {/* <a
        href="https://dash.cloudflare.com/analytics/web"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-2xl hover:bg-orange-100 transition-colors group"
      >
        <div className="flex items-center gap-3">
          <div className="bg-orange-100 border border-orange-200 p-2 rounded-xl">
            <ExternalLink className="w-4 h-4 text-orange-600" />
          </div>
          <div>
            <p className="text-xs font-semibold text-orange-800">Analíticas de visitas</p>
            <p className="text-[10px] text-orange-600 mt-0.5">Ver pageviews, países y dispositivos en Cloudflare Web Analytics</p>
          </div>
        </div>
        <ExternalLink className="w-4 h-4 text-orange-400 group-hover:text-orange-600 transition-colors flex-shrink-0" />
      </a> */}

      {/* Recent WhatsApp leads */}
      <div className="bg-white p-4 lg:p-6 rounded-2xl border border-page-border shadow-sm space-y-4">
        <div className="flex justify-between items-center pb-3 border-b border-page-border">
          <div>
            <h3 className="font-heading font-bold text-base lg:text-lg text-ink">Pedidos por WhatsApp</h3>
            <p className="text-[10px] text-ink-muted mt-0.5">Últimos {stats?.whatsappLeads.recent.length ?? 0} pedidos registrados</p>
          </div>
          {stats && (
            <div className="text-right">
              <p className="text-xs font-bold text-ink">{stats.whatsappLeads.last7d}</p>
              <p className="text-[10px] text-ink-faint">esta semana</p>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-24 text-sm text-ink-muted">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            Cargando...
          </div>
        ) : !stats?.whatsappLeads.recent.length ? (
          <div className="flex flex-col items-center justify-center h-24 text-sm text-ink-muted">
            <TrendingUp className="w-6 h-6 text-ink-faint mb-2" />
            <p>Sin pedidos registrados aún</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {stats.whatsappLeads.recent.map((lead) => (
              <LeadRow key={lead.id} lead={lead} />
            ))}
          </div>
        )}
      </div>

    </div>
  );
};
